// src/components/EditPurchaseOrders.js
import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "../axiosConfig";
import "../styles/EditPurchaseOrders.css";
import Select from "react-select";
import { customSelectStyles } from "../styles/reactSelectStyles.js";
import { FaCalendarAlt, FaClock } from "react-icons/fa";
import { useTranslation } from "react-i18next";

/* ===================== Endpoints ===================== */
const API_BASE = "/purchaseorders";

/* ===================== Helpers (same as Create) ===================== */
const isValidISO2 = (v) => /^[A-Z]{2}$/i.test((v || "").trim());

const toDateInput = (d) => {
  if (!d) return "";
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
};

const addDays = (yyyyMmDd, days) => {
  if (!yyyyMmDd) return "";
  const dt = new Date(`${yyyyMmDd}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return "";
  dt.setDate(dt.getDate() + Number(days || 0));
  return dt.toISOString().slice(0, 10);
};

const diffDays = (from, to) => {
  if (!from || !to) return null;
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const ms = b.getTime() - a.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

const normalizeHour = (raw) => {
  if (raw === "") return "";
  const cleaned = raw.replace(/[^\d:]/g, "");
  if (/^\d{2}:\d{2}$/.test(cleaned)) return cleaned;
  const digits = cleaned.replace(/:/g, "");
  if (/^\d{4}$/.test(digits)) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  return cleaned;
};

const isValidHour = (v) => {
  if (!v) return true; // allow null/empty
  if (!/^\d{2}:\d{2}$/.test(v)) return false;
  const [hh, mm] = v.split(":").map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return false;
  return hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59;
};

const clampDecimal = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  const s = String(v).replace(",", ".");
  if (!/^\d*\.?\d{0,2}$/.test(s)) return s.replace(/[^\d.]/g, "");
  return s;
};

const openNativeDatePicker = (e) => {
  try {
    if (e?.target?.showPicker) e.target.showPicker();
  } catch {
    // ignore
  }
};

export default function EditPurchaseOrders() {
  const navigate = useNavigate();
  const { id } = useParams(); // ✅ /purchaseorders/edit/:id
  const location = useLocation();
  const initialRef = useRef(null);

  const { t } = useTranslation("edit_purchase_orders");

  const fieldLabel = (f) => t(`fields.${f}`, { defaultValue: f });

  const buildChangesSummary = (changedFields) => {
    const fields = Array.isArray(changedFields) ? changedFields : [];
    if (!fields.length) return "";

    if (fields.length === 1) {
      return t("purchaseOrder.changesSummary.single", {
        field: fieldLabel(fields[0]),
        defaultValue: `Campo alterado: ${fieldLabel(fields[0])}.`,
      });
    }

    const head = fields.slice(0, -1).map(fieldLabel).join(", ");
    const last = fieldLabel(fields[fields.length - 1]);
    return t("purchaseOrder.changesSummary.multiple", {
      fields: head,
      last,
      defaultValue: `Campos alterados: ${head} e ${last}.`,
    });
  };

  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrors, setShowErrors] = useState(false); // ✅ só mostra vermelho após clicar Guardar
  const [canAutoCalcDueDate, setCanAutoCalcDueDate] = useState(false); // ✅ evita blink do dueDate

  // Header-ish fields
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState("");
  const [status, setStatus] = useState(""); // ✅ required (like Create) -> start empty

  // Relations (optional)
  const [transportsessionId, setTransportsessionId] = useState("");
  const [loadId, setLoadId] = useState("");

  // Core fields (REQUIRED)
  const [client, setClient] = useState("");

  // Optional general date/hour
  const [date, setDate] = useState("");
  const [hour, setHour] = useState("");

  // Pickup/delivery (REQUIRED)
  const [pickup, setPickup] = useState("");
  const [delivery, setDelivery] = useState("");

  // Timeline pickup/delivery (REQUIRED)
  const [pickupDate, setPickupDate] = useState("");
  const [pickupHour, setPickupHour] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryHour, setDeliveryHour] = useState("");

  // Pricing / VAT (REQUIRED)
  const [price, setPrice] = useState("");
  const [price4subcontrated, setPrice4subcontrated] = useState("");
  const [vatRate, setVatRate] = useState("");
  const [issuerCountry, setIssuerCountry] = useState(""); // ✅ começa vazio para não “piscar”

  // Issue / Payment (REQUIRED)
  const [issueDate, setIssueDate] = useState(""); // ✅ começa vazio para não “piscar”
  const [paymentDueDate, setPaymentDueDate] = useState("");
  const [netDays, setNetDays] = useState(null); // ✅ required (like Create) -> start null
  const [netDaysCustom, setNetDaysCustom] = useState("");

  // Extra (lists) — ADVANCED (OPTIONAL)
  const [pickupCount, setPickupCount] = useState(0);
  const [deliveryCount, setDeliveryCount] = useState(0);
  const [pickupLocation, setPickupLocation] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState("");
  const [pickupListJson, setPickupListJson] = useState("[]");
  const [deliveryListJson, setDeliveryListJson] = useState("[]");

  const [showAdvanced, setShowAdvanced] = useState(false);

  // Validation visuals
  const [invalidFields, setInvalidFields] = useState({});
  const markInvalid = (key, isInvalid) =>
    setInvalidFields((prev) => {
      if (isInvalid) return { ...prev, [key]: true };
      if (!prev[key]) return prev;
      const clone = { ...prev };
      delete clone[key];
      return clone;
    });

  // Field-level errors (same style as Create)
  const [fieldErrors, setFieldErrors] = useState({});

  const clearFieldError = (key) =>
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const clone = { ...prev };
      delete clone[key];
      return clone;
    });

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: "DRAFT", label: t("status.draft", { defaultValue: "Draft" }) },
      { value: "SENT", label: t("status.sent", { defaultValue: "Sent" }) },
      { value: "APPROVED", label: t("status.approved", { defaultValue: "Approved" }) },
      { value: "PAID", label: t("status.paid", { defaultValue: "Paid" }) },
      { value: "CANCELLED", label: t("status.cancelled", { defaultValue: "Cancelled" }) },
    ],
    [t]
  );

  const NET_DAYS_OPTIONS = useMemo(
    () => [
      { value: 0, label: t("netDays.0", { defaultValue: "Due immediately" }) },
      { value: 15, label: t("netDays.15", { defaultValue: "Net 15" }) },
      { value: 30, label: t("netDays.30", { defaultValue: "Net 30" }) },
      { value: 45, label: t("netDays.45", { defaultValue: "Net 45" }) },
      { value: 60, label: t("netDays.60", { defaultValue: "Net 60" }) },
      { value: 90, label: t("netDays.90", { defaultValue: "Net 90" }) },
    ],
    [t]
  );

  const netDaysEffective = useMemo(() => {
    if (netDays === null || netDays === "") return null;
    const custom = Number(netDaysCustom);
    if (netDays === "CUSTOM" && Number.isFinite(custom) && custom >= 0) return custom;
    if (netDays === "CUSTOM") return null;
    return Number(netDays);
  }, [netDays, netDaysCustom]);

  // Auto-calc paymentDueDate when issueDate/netDays changes
  useEffect(() => {
    if (!canAutoCalcDueDate) return; // ✅ não mexer durante a hidratação
    if (!issueDate) return;
    if (netDaysEffective === null) return;
    setPaymentDueDate(addDays(issueDate, netDaysEffective));
  }, [issueDate, netDaysEffective, canAutoCalcDueDate]);

  // ✅ Apply prefill instantly (nice UX), then fetch real data
  useLayoutEffect(() => {
    const prefill = location?.state?.prefill;
    if (!prefill) return;

    // Header
    if (prefill.purchaseOrderNumber != null) setPurchaseOrderNumber(String(prefill.purchaseOrderNumber));
    if (prefill.status != null) setStatus(String(prefill.status).toUpperCase());

    // Relations
    if (prefill.transportsessionId != null) setTransportsessionId(String(prefill.transportsessionId));
    if (prefill.loadId != null) setLoadId(String(prefill.loadId));

    // Core
    if (prefill.client != null) setClient(String(prefill.client || ""));

    // Optional date/hour
    if (prefill.date != null) setDate(toDateInput(prefill.date));
    if (prefill.hour != null) setHour(prefill.hour ?? "");

    // Pickup/Delivery
    if (prefill.pickup != null) setPickup(String(prefill.pickup || ""));
    if (prefill.delivery != null) setDelivery(String(prefill.delivery || ""));

    // Timeline pickup/delivery
    if (prefill.pickupDate != null) setPickupDate(toDateInput(prefill.pickupDate));
    if (prefill.pickupHour != null) setPickupHour(prefill.pickupHour ?? "");
    if (prefill.deliveryDate != null) setDeliveryDate(toDateInput(prefill.deliveryDate));
    if (prefill.deliveryHour != null) setDeliveryHour(prefill.deliveryHour ?? "");

    // Pricing / VAT
    if (prefill.price != null) setPrice(prefill.price === "" ? "" : String(prefill.price));
    if (prefill.price4subcontrated != null)
      setPrice4subcontrated(prefill.price4subcontrated === "" ? "" : String(prefill.price4subcontrated));

    if (prefill.vatRate != null) setVatRate(prefill.vatRate === "" ? "" : String(prefill.vatRate));
    if (prefill.issuerCountry != null) setIssuerCountry(String(prefill.issuerCountry || "").toUpperCase());

    // Issue / payment
    if (prefill.issueDate != null) setIssueDate(toDateInput(prefill.issueDate));
    if (prefill.paymentDueDate != null) setPaymentDueDate(toDateInput(prefill.paymentDueDate));

    // Net days (se vier)
    if (prefill.net_payment_days_carrier != null && prefill.net_payment_days_carrier !== "") {
      const n = Number(prefill.net_payment_days_carrier);
      const matches = NET_DAYS_OPTIONS.some((x) => Number(x.value) === n);
      if (matches) {
        setNetDays(n);
        setNetDaysCustom("");
      } else {
        setNetDays("CUSTOM");
        setNetDaysCustom(String(n));
      }
    }

    // Advanced (se vier)
    if (prefill.pickupCount != null) setPickupCount(Number(prefill.pickupCount || 0));
    if (prefill.deliveryCount != null) setDeliveryCount(Number(prefill.deliveryCount || 0));
    if (prefill.pickupLocation != null) setPickupLocation(String(prefill.pickupLocation || ""));
    if (prefill.deliveryLocation != null) setDeliveryLocation(String(prefill.deliveryLocation || ""));

    if (prefill.pickupList != null) setPickupListJson(JSON.stringify(prefill.pickupList ?? [], null, 0));
    if (prefill.deliveryList != null) setDeliveryListJson(JSON.stringify(prefill.deliveryList ?? [], null, 0));
  }, [location?.state?.prefill, NET_DAYS_OPTIONS]);

  useEffect(() => {
    let mounted = true;

    // ✅ se existe prefill, já tens UI instantânea — não mostrar "A carregar..."
    const hasPrefill = !!location?.state?.prefill;

    (async () => {
      if (!hasPrefill) setFetching(true); // ✅ só mostra loading quando não há nada para mostrar
      setMessage("");

      try {
        const { data } = await axios.get(`${API_BASE}/${id}`, { params: { t: Date.now() } });
        const po = data?.purchaseOrder ?? data ?? null;
        if (!mounted || !po) return;

        // snapshot initial normalized
        initialRef.current = {
          status: String(po.status ?? "").toUpperCase() || null,
          transportsessionId: po.transportsessionId != null ? Number(po.transportsessionId) : null,
          loadId: po.loadId != null ? Number(po.loadId) : null,
          client: String(po.client ?? po.supplier ?? po.vendor ?? "") || null,

          date: toDateInput(po.date) || null,
          hour: po.hour ?? null,

          pickup: String(po.pickup ?? "") || null,
          delivery: String(po.delivery ?? "") || null,

          price: po.price4subcontrated == null
            ? (po.price == null ? null : Number(po.price))
            : Number(po.price4subcontrated),

          price4subcontrated: po.price4subcontrated == null ? null : Number(po.price4subcontrated),

          vatRate: po.vatRate == null ? null : Number(po.vatRate),
          issuerCountry: (po.issuerCountry ? String(po.issuerCountry).toUpperCase() : "PT") || null,

          issueDate: toDateInput(po.issueDate) || null,
          paymentDueDate: toDateInput(po.paymentDueDate) || null,

          pickupDate: toDateInput(po.pickupDate) || null,
          pickupHour: po.pickupHour ?? null,
          deliveryDate: toDateInput(po.deliveryDate) || null,
          deliveryHour: po.deliveryHour ?? null,

          net_payment_days_carrier: po.net_payment_days_carrier == null ? null : Number(po.net_payment_days_carrier),

          pickupCount: Number(po.pickupCount || 0),
          deliveryCount: Number(po.deliveryCount || 0),
          pickupLocation: po.pickupLocation || null,
          deliveryLocation: po.deliveryLocation || null,

          pickupList: Array.isArray(po.pickupList) ? po.pickupList : null,
          deliveryList: Array.isArray(po.deliveryList) ? po.deliveryList : null,
        };

        setPurchaseOrderNumber(
          po.purchaseOrderNumber != null
            ? String(po.purchaseOrderNumber)
            : po.poNumber != null
            ? String(po.poNumber)
            : ""
        );

        setStatus(String(po.status ?? "").toUpperCase());

        setTransportsessionId(po.transportsessionId != null ? String(po.transportsessionId) : "");
        setLoadId(po.loadId != null ? String(po.loadId) : "");

        setClient(String(po.client ?? po.supplier ?? po.vendor ?? ""));

        setDate(toDateInput(po.date));
        setHour(po.hour ?? "");

        setPickup(String(po.pickup ?? ""));
        setDelivery(String(po.delivery ?? ""));

        setPickupDate(toDateInput(po.pickupDate));
        setPickupHour(po.pickupHour ?? "");
        setDeliveryDate(toDateInput(po.deliveryDate));
        setDeliveryHour(po.deliveryHour ?? "");

        const p4 = po.price4subcontrated;
        const pLegacy = po.price;

        setPrice(p4 != null ? String(p4) : pLegacy != null ? String(pLegacy) : "");
        setPrice4subcontrated(p4 == null ? "" : String(p4));

        setVatRate(po.vatRate == null ? "" : String(po.vatRate));
        setIssuerCountry(po.issuerCountry ? String(po.issuerCountry).toUpperCase() : "PT");

        const issue = toDateInput(po.issueDate);
        const due = toDateInput(po.paymentDueDate);

        setIssueDate(issue || toDateInput(new Date()));
        setPaymentDueDate(due || "");

        // net days UI
        const nd = po.net_payment_days_carrier;
        if (nd != null && nd !== "") {
          const n = Number(nd);
          const matches = NET_DAYS_OPTIONS.some((x) => Number(x.value) === n);
          if (matches) {
            setNetDays(n);
            setNetDaysCustom("");
          } else {
            setNetDays("CUSTOM");
            setNetDaysCustom(String(n));
          }
        } else {
          const d = diffDays(issue, due);
          if (d != null && d >= 0) {
            const matches = NET_DAYS_OPTIONS.some((x) => Number(x.value) === d);
            if (matches) {
              setNetDays(d);
              setNetDaysCustom("");
            } else {
              setNetDays("CUSTOM");
              setNetDaysCustom(String(d));
            }
          } else {
            setNetDays(null);
            setNetDaysCustom("");
          }
        }

        setPickupCount(Number(po.pickupCount || 0));
        setDeliveryCount(Number(po.deliveryCount || 0));
        setPickupLocation(po.pickupLocation || "");
        setDeliveryLocation(po.deliveryLocation || "");

        setPickupListJson(JSON.stringify(po.pickupList ?? [], null, 0));
        setDeliveryListJson(JSON.stringify(po.deliveryList ?? [], null, 0));
        setCanAutoCalcDueDate(true); // ✅ agora já pode auto-ajustar sem “piscar”
      } catch (err) {
        console.error("❌ Failed to fetch purchase order:", err);
        if (mounted) setMessage(t("messages.failed", { defaultValue: "Falha ao carregar o pedido de compra." }));
      } finally {
        if (mounted) setFetching(false); // ✅ sempre acaba fetching
      }
    })();

    return () => {
      mounted = false;
    };
  }, [id, t, NET_DAYS_OPTIONS, location?.state]);

  const validate = () => {
    const nextInvalid = {};
    const nextErrors = {};

    const reqLabel = (label, plural = false) =>
      plural
        ? t("validation.required_plural", { field: label, defaultValue: `${label} são obrigatórios.` })
        : t("validation.required_singular", { field: label, defaultValue: `${label} é obrigatório.` });

    const req = (key, value, fieldLabelKey, plural = false) => {
      const empty = !String(value ?? "").trim();
      if (empty) {
        nextInvalid[key] = true;
        nextErrors[key] = reqLabel(t(fieldLabelKey, { defaultValue: fieldLabelKey }), plural);
        return false;
      }
      return true;
    };

    // REQUIRED (same as Create)
    req("client", client, "fields.client");
    req("status", status, "fields.status");
    req("netDays", netDays, "fields.paymentTerms", true);

    if (netDays === "CUSTOM") {
      req("netDaysCustom", netDaysCustom, "fields.paymentTerms", true);
    }

    if (req("issuerCountry", issuerCountry, "fields.issuerCountry")) {
      if (!isValidISO2(issuerCountry)) {
        nextInvalid.issuerCountry = true;
        nextErrors.issuerCountry = reqLabel(t("fields.issuerCountry"));
      }
    }

    if (req("vatRate", vatRate, "fields.vatRate")) {
      const n = Number(String(vatRate).replace(",", "."));
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        nextInvalid.vatRate = true;
        nextErrors.vatRate = reqLabel(t("fields.vatRate"));
      }
    }

    if (req("price", price, "fields.price")) {
      const n = Number(String(price).replace(",", "."));
      if (!Number.isFinite(n) || n < 0) {
        nextInvalid.price = true;
        nextErrors.price = reqLabel(t("fields.price"));
      }
    }

    if (req("price4subcontrated", price4subcontrated, "fields.price4subcontrated")) {
      const n = Number(String(price4subcontrated).replace(",", "."));
      if (!Number.isFinite(n) || n < 0) {
        nextInvalid.price4subcontrated = true;
        nextErrors.price4subcontrated = reqLabel(t("fields.price4subcontrated"));
      }
    }

    req("issueDate", issueDate, "fields.issueDate");
    req("paymentDueDate", paymentDueDate, "fields.paymentDueDate");

    // OPTIONAL: date/hour (only validate hour format if provided)
    if (String(hour ?? "").trim() && !isValidHour(hour)) {
      nextInvalid.hour = true;
      nextErrors.hour = reqLabel(t("fields.hourOptional"));
    }

    // REQUIRED: pickup/delivery timeline
    req("pickup", pickup, "fields.pickup");
    req("pickupDate", pickupDate, "fields.pickupDate");
    if (req("pickupHour", pickupHour, "fields.pickupHour")) {
      if (!isValidHour(pickupHour)) {
        nextInvalid.pickupHour = true;
        nextErrors.pickupHour = reqLabel(t("fields.pickupHour"));
      }
    }

    req("delivery", delivery, "fields.delivery");
    req("deliveryDate", deliveryDate, "fields.deliveryDate");
    if (req("deliveryHour", deliveryHour, "fields.deliveryHour")) {
      if (!isValidHour(deliveryHour)) {
        nextInvalid.deliveryHour = true;
        nextErrors.deliveryHour = reqLabel(t("fields.deliveryHour"));
      }
    }

    // PaymentTerms: if CUSTOM and user typed invalid -> same message
    if (netDays === "CUSTOM" && String(netDaysCustom ?? "").trim()) {
      const n = Number(netDaysCustom);
      if (!Number.isFinite(n) || n < 0) {
        nextInvalid.netDaysCustom = true;
        nextErrors.netDaysCustom = reqLabel(t("fields.paymentTerms"), true);
      }
    }

    // ADVANCED: optional. Only validate JSON if textarea has content
    if (showAdvanced) {
      const hasPickupList = String(pickupListJson ?? "").trim().length > 0;
      if (hasPickupList) {
        try {
          const arr = JSON.parse(pickupListJson);
          if (!Array.isArray(arr)) {
            nextInvalid.pickupList = true;
            nextErrors.pickupList = reqLabel(t("fields.pickupList"));
          }
        } catch {
          nextInvalid.pickupList = true;
          nextErrors.pickupList = reqLabel(t("fields.pickupList"));
        }
      }

      const hasDeliveryList = String(deliveryListJson ?? "").trim().length > 0;
      if (hasDeliveryList) {
        try {
          const arr = JSON.parse(deliveryListJson);
          if (!Array.isArray(arr)) {
            nextInvalid.deliveryList = true;
            nextErrors.deliveryList = reqLabel(t("fields.deliveryList"));
          }
        } catch {
          nextInvalid.deliveryList = true;
          nextErrors.deliveryList = reqLabel(t("fields.deliveryList"));
        }
      }
    }

    setInvalidFields(nextInvalid);
    setFieldErrors(nextErrors);

    if (Object.keys(nextInvalid).length > 0) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setErrorMessage("");

    setShowErrors(true); // ✅ a partir daqui, mostrar erros visuais

    if (!validate()) return;

    setSaving(true);
    try {
      let net_payment_days_carrier = null;
      if (netDaysEffective !== null) {
        net_payment_days_carrier = netDaysEffective;
      } else {
        const d = diffDays(issueDate, paymentDueDate);
        if (d !== null && d >= 0) net_payment_days_carrier = d;
      }

      const full = {
        status,
        transportsessionId: transportsessionId ? Number(transportsessionId) : null,
        loadId: loadId ? Number(loadId) : null,

        client: client.trim() ? client.trim() : null,

        date: date || null,
        hour: hour || null,

        pickup: pickup.trim() ? pickup.trim() : null,
        delivery: delivery.trim() ? delivery.trim() : null,

        // ✅ backend usa price4subcontrated como campo real
        price4subcontrated:
          price4subcontrated !== ""
            ? Number(String(price4subcontrated).replace(",", "."))
            : null,

        vatRate: vatRate === "" ? null : Number(String(vatRate).replace(",", ".")),
        issuerCountry: issuerCountry ? issuerCountry.toUpperCase() : null,

        issueDate: issueDate || null,
        paymentDueDate: paymentDueDate || null,

        pickupDate: pickupDate || null,
        pickupHour: pickupHour || null,
        deliveryDate: deliveryDate || null,
        deliveryHour: deliveryHour || null,

        net_payment_days_carrier,

        pickupCount: Number(pickupCount || 0),
        deliveryCount: Number(deliveryCount || 0),

        pickupLocation: pickupLocation.trim() ? pickupLocation.trim() : null,
        deliveryLocation: deliveryLocation.trim() ? deliveryLocation.trim() : null,

        pickupList: showAdvanced ? JSON.parse(pickupListJson || "[]") : undefined,
        deliveryList: showAdvanced ? JSON.parse(deliveryListJson || "[]") : undefined,
      };

      Object.keys(full).forEach((k) => full[k] === undefined && delete full[k]);

      // diff vs snapshot inicial -> payload limpo
      const beforeSnap = initialRef.current || {};
      const payload = {};

      const norm = (v) => {
        if (v === undefined) return "__UNDEF__";
        if (v === null) return "__NULL__";
        if (typeof v === "string") return v.trim();
        if (typeof v === "number") return Number.isFinite(v) ? v : String(v);
        if (typeof v === "object") {
          try {
            return JSON.stringify(v);
          } catch {
            return String(v);
          }
        }
        return v;
      };

      for (const k of Object.keys(full)) {
        if (norm(full[k]) !== norm(beforeSnap[k])) payload[k] = full[k];
      }

      if (!Object.keys(payload).length) {
        setErrorMessage(t("messages.noChanges", { defaultValue: "Sem alterações para guardar." }));
        setSaving(false);
        return;
      }

      await axios.put(`${API_BASE}/${id}`, payload);

      // opcional: summary de campos alterados
      const summary = buildChangesSummary(Object.keys(payload));
      setMessage(summary || "");

      setErrorMessage("");
      setShowSuccessPopup(true);

      setTimeout(() => {
        navigate("/purchaseorders");
      }, 1200);
    } catch (err) {
      console.error("EditPurchaseOrders error:", err);
      const rawError = err?.response?.data?.error;
      const errorKey = err?.response?.data?.errorKey;

      setShowSuccessPopup(false);

      if (errorKey) {
        setErrorMessage(t(errorKey));
      } else if (rawError) {
        setErrorMessage(
          t("messages.failedWithReason", {
            defaultValue: `Falhou: ${String(rawError)}`,
            reason: String(rawError),
          })
        );
      } else {
        setErrorMessage(t("messages.failed", { defaultValue: "Falha ao guardar." }));
      }
    } finally {
      setSaving(false);
    }
  };

return (
  <div className="edit-purchase-orders-page">
    <div
      className="edit-purchase-orders-back-arrow"
      onClick={() => navigate("/purchaseorders")}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") navigate("/purchaseorders");
      }}
    >
      ← {t("page.back", { defaultValue: "Voltar" })}
    </div>

    <div className="edit-purchase-orders-header-top">
      <h2 className="edit-purchase-orders-title">
        {t("editTitle", { defaultValue: "Editar Pedido de Compra" })}
      </h2>
    </div>

    {errorMessage && (
      <p className="edit-purchase-orders-error-message">{errorMessage}</p>
    )}

    {showSuccessPopup && (
      <div className="edit-purchase-orders-successPopup-overlay">
        <div className="edit-purchase-orders-successPopup">
          <div className="edit-purchase-orders-successPopup-icon">✓</div>

          <h3>
            {t("success.updatedTitle", {
              defaultValue: "Pedido de Compra atualizado",
            })}
          </h3>

          <p>
            {t("success.updated", {
              defaultValue: "Pedido de Compra atualizado com sucesso.",
            })}
          </p>

          {message && (
            <p className="edit-purchase-orders-successPopup-note">
              {message}
            </p>
          )}

          <button
            type="button"
            onClick={() => navigate("/purchaseorders")}
            className="edit-purchase-orders-successPopup-btn"
          >
            {t("actions.ok", {
              defaultValue: "OK",
            })}
          </button>
        </div>
      </div>
    )}

    <div className="edit-purchase-orders-card">
      <div className="edit-purchase-orders-header" />

      <form onSubmit={handleSubmit} className="edit-purchase-orders-form">
        {/* ROW 1 — PO / Status / Client */}
        <div className="edit-purchase-orders-row">
          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.purchaseOrderNumber")}</label>
            <input
              type="text"
              value={purchaseOrderNumber}
              readOnly
              placeholder={t("placeholders.auto")}
              className="edit-purchase-orders-input"
            />
          </div>

          <div className="edit-purchase-orders-field edit-purchase-orders-dropdown-field">
            <label className="edit-purchase-orders-label">{t("fields.status")}*</label>

            <div
              className={`edit-purchase-orders-dropdown-wrap ${
                showErrors && invalidFields.status ? "edit-purchase-orders-dropdown-error" : ""
              }`}
            >
              <Select
                classNamePrefix="react-select"
                styles={customSelectStyles}
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((o) => o.value === status) || null}
                onChange={(opt) => {
                  const v = opt?.value ?? "";
                  setStatus(v);
                  if (!showErrors) return; // ✅
                  const bad = !String(v).trim();
                  markInvalid("status", bad);
                  if (!bad) clearFieldError("status");
                }}
                isClearable
                placeholder={t("placeholders.status", { defaultValue: "Selecionar..." })}
              />
            </div>

            {showErrors && fieldErrors.status && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.status}</div>
            )}
          </div>

          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.client")}*</label>
            <input
              type="text"
              value={client}
              onChange={(e) => {
                const v = e.target.value;
                setClient(v);

                if (!showErrors) return; // ✅ não mostrar vermelho antes de Guardar

                const bad = !v.trim();
                markInvalid("client", bad);
                if (!bad) clearFieldError("client");
              }}
              className={`edit-purchase-orders-input ${
                showErrors && invalidFields.client ? "edit-purchase-orders-input-error" : ""
              }`}
              placeholder={t("placeholders.client")}
            />
            {showErrors && fieldErrors.client && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.client}</div>
            )}
          </div>
        </div>

        {/* ROW 2 — Issuer / VAT / Price */}
        <div className="edit-purchase-orders-row">
          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.issuerCountry")}*</label>
            <input
              type="text"
              value={issuerCountry}
              onChange={(e) => {
                const v = e.target.value.toUpperCase().slice(0, 2);
                setIssuerCountry(v);

                if (!showErrors) return; // ✅

                const bad = !v.trim() || !isValidISO2(v);
                markInvalid("issuerCountry", bad);
                if (!bad) clearFieldError("issuerCountry");
              }}
              className={`edit-purchase-orders-input ${
                showErrors && invalidFields.issuerCountry ? "edit-purchase-orders-input-error" : ""
              }`}
              placeholder={t("placeholders.issuerCountry")}
            />
            {showErrors && fieldErrors.issuerCountry && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.issuerCountry}</div>
            )}
          </div>

          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.vatRate")}*</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={vatRate}
              onChange={(e) => {
                const v = e.target.value;
                setVatRate(v);

                if (!showErrors) return; // ✅

                const n = Number(String(v).replace(",", "."));
                const bad = !String(v).trim() || !Number.isFinite(n) || n < 0 || n > 100;
                markInvalid("vatRate", bad);
                if (!bad) clearFieldError("vatRate");
              }}
              className={`edit-purchase-orders-input ${
                showErrors && invalidFields.vatRate ? "edit-purchase-orders-input-error" : ""
              }`}
              placeholder={t("placeholders.vatRate")}
            />
            {showErrors && fieldErrors.vatRate && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.vatRate}</div>
            )}
          </div>

          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.price")}*</label>
            <input
              type="text"
              value={price}
              onChange={(e) => {
                const v = clampDecimal(e.target.value);
                setPrice(v);

                if (!showErrors) return; // ✅

                const n = Number(String(v).replace(",", "."));
                const bad = !String(v).trim() || !Number.isFinite(n) || n < 0;
                markInvalid("price", bad);
                if (!bad) clearFieldError("price");
              }}
              className={`edit-purchase-orders-input ${
                showErrors && invalidFields.price ? "edit-purchase-orders-input-error" : ""
              }`}
              placeholder={t("placeholders.price")}
            />
            {showErrors && fieldErrors.price && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.price}</div>
            )}
          </div>
        </div>

        {/* ROW 3 — Price4Sub / IssueDate / PaymentTerms */}
        <div className="edit-purchase-orders-row">
          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.price4subcontrated")}*</label>
            <input
              type="text"
              value={price4subcontrated}
              onChange={(e) => {
                const v = clampDecimal(e.target.value);
                setPrice4subcontrated(v);

                if (!showErrors) return; // ✅

                const n = Number(String(v).replace(",", "."));
                const bad = !String(v).trim() || !Number.isFinite(n) || n < 0;
                markInvalid("price4subcontrated", bad);
                if (!bad) clearFieldError("price4subcontrated");
              }}
              className={`edit-purchase-orders-input ${
                showErrors && invalidFields.price4subcontrated ? "edit-purchase-orders-input-error" : ""
              }`}
              placeholder={t("placeholders.price")}
            />
            {showErrors && fieldErrors.price4subcontrated && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.price4subcontrated}</div>
            )}
          </div>

          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.issueDate")}*</label>
            <div className="edit-purchase-orders-input-with-icon">
              <FaCalendarAlt className="edit-purchase-orders-icon" />
              <input
                type="date"
                value={issueDate}
                onClick={openNativeDatePicker}
                onFocus={openNativeDatePicker}
                onChange={(e) => {
                  const v = e.target.value;
                  setIssueDate(v);

                  if (!showErrors) return; // ✅

                  const bad = !v;
                  markInvalid("issueDate", bad);
                  if (!bad) clearFieldError("issueDate");
                }}
                className={`edit-purchase-orders-input ${
                  showErrors && invalidFields.issueDate ? "edit-purchase-orders-input-error" : ""
                }`}
              />
            </div>
            {showErrors && fieldErrors.issueDate && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.issueDate}</div>
            )}
          </div>

          <div className="edit-purchase-orders-field edit-purchase-orders-dropdown-field">
            <label className="edit-purchase-orders-label">{t("fields.paymentTerms")}*</label>

            <div
              className={`edit-purchase-orders-dropdown-wrap ${
                showErrors && invalidFields.netDays ? "edit-purchase-orders-dropdown-error" : ""
              }`}
            >
              <Select
                classNamePrefix="react-select"
                styles={customSelectStyles}
                options={[...NET_DAYS_OPTIONS, { value: "CUSTOM", label: t("netDays.custom") }]}
                value={
                  netDays === "CUSTOM"
                    ? { value: "CUSTOM", label: t("netDays.custom") }
                    : NET_DAYS_OPTIONS.find((o) => o.value === netDays) || null
                }
                onChange={(opt) => {
                  const v = opt?.value ?? null;
                  setNetDays(v);
                  if (!showErrors) return; // ✅ primeiro!
                  const bad = v === null || v === "";
                  markInvalid("netDays", bad);
                  if (!bad) clearFieldError("netDays");

                  if (v !== "CUSTOM") {
                    setNetDaysCustom("");
                    markInvalid("netDaysCustom", false);
                    clearFieldError("netDaysCustom");
                  }
                }}
                isClearable
                placeholder={t("placeholders.paymentTerms", { defaultValue: "Selecionar..." })}
              />
            </div>

            {showErrors && fieldErrors.netDays && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.netDays}</div>
            )}

            {netDays === "CUSTOM" && (
              <>
                <input
                  type="number"
                  min="0"
                  value={netDaysCustom}
                  onChange={(e) => {
                    const v = e.target.value;
                    setNetDaysCustom(v);

                    if (!showErrors) return; // ✅

                    const bad = !String(v ?? "").trim();
                    markInvalid("netDaysCustom", bad);
                    if (!bad) clearFieldError("netDaysCustom");
                  }}
                  className={`edit-purchase-orders-input edit-purchase-orders-mt-8 ${
                    showErrors && invalidFields.netDaysCustom ? "edit-purchase-orders-input-error" : ""
                  }`}
                  placeholder={t("placeholders.netDays")}
                />

                {showErrors && fieldErrors.netDaysCustom && (
                  <div className="edit-purchase-orders-field-error">{fieldErrors.netDaysCustom}</div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ROW 4 — DueDate / Optional Date / Optional Hour */}
        <div className="edit-purchase-orders-row">
          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.paymentDueDate")}*</label>
            <div className="edit-purchase-orders-input-with-icon">
              <FaCalendarAlt className="edit-purchase-orders-icon" />
              <input
                type="date"
                value={paymentDueDate}
                onClick={openNativeDatePicker}
                onFocus={openNativeDatePicker}
                onChange={(e) => {
                  const v = e.target.value;
                  setPaymentDueDate(v);

                  if (!showErrors) return; // ✅

                  const bad = !v;
                  markInvalid("paymentDueDate", bad);
                  if (!bad) clearFieldError("paymentDueDate");
                }}
                className={`edit-purchase-orders-input ${
                  showErrors && invalidFields.paymentDueDate ? "edit-purchase-orders-input-error" : ""
                }`}
              />
            </div>
            {showErrors && fieldErrors.paymentDueDate && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.paymentDueDate}</div>
            )}
          </div>

          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.dateOptional")}</label>
            <div className="edit-purchase-orders-input-with-icon">
              <FaCalendarAlt className="edit-purchase-orders-icon" />
              <input
                type="date"
                value={date}
                onClick={openNativeDatePicker}
                onFocus={openNativeDatePicker}
                onChange={(e) => {
                  const v = e.target.value;
                  setDate(v);
                  markInvalid("date", false);
                  clearFieldError("date");
                }}
                className="edit-purchase-orders-input"
              />
            </div>
          </div>

          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.hourOptional")}</label>
            <div className="edit-purchase-orders-input-with-icon">
              <FaClock className="edit-purchase-orders-icon" />
              <input
                type="text"
                value={hour}
                onChange={(e) => {
                  const v = normalizeHour(e.target.value);
                  setHour(v);

                  if (!showErrors) return; // ✅

                  const bad = String(v).trim() !== "" && !isValidHour(v);
                  markInvalid("hour", bad);
                  if (!bad) clearFieldError("hour");
                }}
                className={`edit-purchase-orders-input ${
                  showErrors && invalidFields.hour ? "edit-purchase-orders-input-error" : ""
                }`}
                placeholder={t("placeholders.hour")}
              />
            </div>
            {showErrors && fieldErrors.hour && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.hour}</div>
            )}
          </div>
        </div>

        {/* ROW 5 — Pickup / Pickup Date / Pickup Hour */}
        <div className="edit-purchase-orders-row">
          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.pickup")}*</label>
            <input
              type="text"
              value={pickup}
              onChange={(e) => {
                const v = e.target.value;
                setPickup(v);

                if (!showErrors) return; // ✅

                const bad = !String(v ?? "").trim();
                markInvalid("pickup", bad);
                if (!bad) clearFieldError("pickup");
              }}
              className={`edit-purchase-orders-input ${
                showErrors && invalidFields.pickup ? "edit-purchase-orders-input-error" : ""
              }`}
              placeholder={t("placeholders.pickup")}
            />
            {showErrors && fieldErrors.pickup && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.pickup}</div>
            )}
          </div>

          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.pickupDate")}*</label>
            <div className="edit-purchase-orders-input-with-icon">
              <FaCalendarAlt className="edit-purchase-orders-icon" />
              <input
                type="date"
                value={pickupDate}
                onClick={openNativeDatePicker}
                onFocus={openNativeDatePicker}
                onChange={(e) => {
                  const v = e.target.value;
                  setPickupDate(v);

                  if (!showErrors) return; // ✅

                  const bad = !v;
                  markInvalid("pickupDate", bad);
                  if (!bad) clearFieldError("pickupDate");
                }}
                className={`edit-purchase-orders-input ${
                  showErrors && invalidFields.pickupDate ? "edit-purchase-orders-input-error" : ""
                }`}
              />
            </div>
            {showErrors && fieldErrors.pickupDate && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.pickupDate}</div>
            )}
          </div>

          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.pickupHour")}*</label>
            <div className="edit-purchase-orders-input-with-icon">
              <FaClock className="edit-purchase-orders-icon" />
              <input
                type="text"
                value={pickupHour}
                onChange={(e) => {
                  const v = normalizeHour(e.target.value);
                  setPickupHour(v);

                  if (!showErrors) return; // ✅

                  const bad = !String(v).trim() || !isValidHour(v);
                  markInvalid("pickupHour", bad);
                  if (!bad) clearFieldError("pickupHour");
                }}
                className={`edit-purchase-orders-input ${
                  showErrors && invalidFields.pickupHour ? "edit-purchase-orders-input-error" : ""
                }`}
                placeholder={t("placeholders.hour")}
              />
            </div>
            {showErrors && fieldErrors.pickupHour && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.pickupHour}</div>
            )}
          </div>
        </div>

        {/* ROW 6 — Delivery / Delivery Date / Delivery Hour */}
        <div className="edit-purchase-orders-row">
          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.delivery")}*</label>
            <input
              type="text"
              value={delivery}
              onChange={(e) => {
                const v = e.target.value;
                setDelivery(v);

                if (!showErrors) return; // ✅

                const bad = !String(v ?? "").trim();
                markInvalid("delivery", bad);
                if (!bad) clearFieldError("delivery");
              }}
              className={`edit-purchase-orders-input ${
                showErrors && invalidFields.delivery ? "edit-purchase-orders-input-error" : ""
              }`}
              placeholder={t("placeholders.delivery")}
            />
            {showErrors && fieldErrors.delivery && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.delivery}</div>
            )}
          </div>

          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.deliveryDate")}*</label>
            <div className="edit-purchase-orders-input-with-icon">
              <FaCalendarAlt className="edit-purchase-orders-icon" />
              <input
                type="date"
                value={deliveryDate}
                onClick={openNativeDatePicker}
                onFocus={openNativeDatePicker}
                onChange={(e) => {
                  const v = e.target.value;
                  setDeliveryDate(v);

                  if (!showErrors) return; // ✅

                  const bad = !v;
                  markInvalid("deliveryDate", bad);
                  if (!bad) clearFieldError("deliveryDate");
                }}
                className={`edit-purchase-orders-input ${
                  showErrors && invalidFields.deliveryDate ? "edit-purchase-orders-input-error" : ""
                }`}
              />
            </div>
            {showErrors && fieldErrors.deliveryDate && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.deliveryDate}</div>
            )}
          </div>

          <div className="edit-purchase-orders-field">
            <label className="edit-purchase-orders-label">{t("fields.deliveryHour")}*</label>
            <div className="edit-purchase-orders-input-with-icon">
              <FaClock className="edit-purchase-orders-icon" />
              <input
                type="text"
                value={deliveryHour}
                onChange={(e) => {
                  const v = normalizeHour(e.target.value);
                  setDeliveryHour(v);

                  if (!showErrors) return; // ✅

                  const bad = !String(v).trim() || !isValidHour(v);
                  markInvalid("deliveryHour", bad);
                  if (!bad) clearFieldError("deliveryHour");
                }}
                className={`edit-purchase-orders-input ${
                  showErrors && invalidFields.deliveryHour ? "edit-purchase-orders-input-error" : ""
                }`}
                placeholder={t("placeholders.hour")}
              />
            </div>
            {showErrors && fieldErrors.deliveryHour && (
              <div className="edit-purchase-orders-field-error">{fieldErrors.deliveryHour}</div>
            )}
          </div>
        </div>

        {/* ADVANCED TOGGLE */}
        <div className="edit-purchase-orders-advanced-toggle-row">
          <button
            type="button"
            className="edit-purchase-orders-secondary-btn"
            onClick={() => setShowAdvanced((s) => !s)}
          >
            {showAdvanced ? t("actions.hideAdvanced") : t("actions.showAdvanced")}
          </button>

          <div className="edit-purchase-orders-advanced-hint">{t("advanced.hint")}</div>
        </div>

        {/* ADVANCED SECTION */}
        {showAdvanced && (
          <div className="edit-purchase-orders-advanced">
            {/* PICKUP — optional */}
            <div className="edit-purchase-orders-row edit-purchase-orders-row-pd">
              <div className="edit-purchase-orders-field">
                <label className="edit-purchase-orders-label">{t("fields.pickupCount")}</label>
                <input
                  type="number"
                  min="0"
                  value={pickupCount}
                  onChange={(e) => setPickupCount(e.target.value)}
                  className="edit-purchase-orders-input"
                />
              </div>

              <div className="edit-purchase-orders-field">
                <label className="edit-purchase-orders-label">{t("fields.pickupLocation")}</label>
                <input
                  type="text"
                  value={pickupLocation}
                  onChange={(e) => setPickupLocation(e.target.value)}
                  className="edit-purchase-orders-input"
                />
              </div>

              <div className="edit-purchase-orders-field">
                <label className="edit-purchase-orders-label">{t("fields.pickupList")}</label>
                <textarea
                  rows={1}
                  value={pickupListJson}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPickupListJson(v);

                    if (!showErrors) return; // ✅

                    const has = String(v ?? "").trim().length > 0;
                    if (!has) {
                      markInvalid("pickupList", false);
                      clearFieldError("pickupList");
                      return;
                    }

                    try {
                      const arr = JSON.parse(v);
                      const bad = !Array.isArray(arr);
                      markInvalid("pickupList", bad);
                      if (!bad) clearFieldError("pickupList");
                    } catch {
                      markInvalid("pickupList", true);
                    }
                  }}
                  className={`edit-purchase-orders-input edit-purchase-orders-textarea-compact ${
                    showErrors && invalidFields.pickupList ? "edit-purchase-orders-input-error" : ""
                  }`}
                  placeholder={t("placeholders.pickupList")}
                />
                {showErrors && fieldErrors.pickupList && (
                  <div className="edit-purchase-orders-field-error">{fieldErrors.pickupList}</div>
                )}
              </div>
            </div>

            {/* DELIVERY — optional */}
            <div className="edit-purchase-orders-row edit-purchase-orders-row-pd">
              <div className="edit-purchase-orders-field">
                <label className="edit-purchase-orders-label">{t("fields.deliveryCount")}</label>
                <input
                  type="number"
                  min="0"
                  value={deliveryCount}
                  onChange={(e) => setDeliveryCount(e.target.value)}
                  className="edit-purchase-orders-input"
                />
              </div>

              <div className="edit-purchase-orders-field">
                <label className="edit-purchase-orders-label">{t("fields.deliveryLocation")}</label>
                <input
                  type="text"
                  value={deliveryLocation}
                  onChange={(e) => setDeliveryLocation(e.target.value)}
                  className="edit-purchase-orders-input"
                />
              </div>

              <div className="edit-purchase-orders-field">
                <label className="edit-purchase-orders-label">{t("fields.deliveryList")}</label>
                <textarea
                  rows={1}
                  value={deliveryListJson}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDeliveryListJson(v);

                    if (!showErrors) return; // ✅

                    const has = String(v ?? "").trim().length > 0;
                    if (!has) {
                      markInvalid("deliveryList", false);
                      clearFieldError("deliveryList");
                      return;
                    }

                    try {
                      const arr = JSON.parse(v);
                      const bad = !Array.isArray(arr);
                      markInvalid("deliveryList", bad);
                      if (!bad) clearFieldError("deliveryList");
                    } catch {
                      markInvalid("deliveryList", true);
                    }
                  }}
                  className={`edit-purchase-orders-input edit-purchase-orders-textarea-compact ${
                    showErrors && invalidFields.deliveryList ? "edit-purchase-orders-input-error" : ""
                  }`}
                  placeholder={t("placeholders.deliveryList")}
                />
                {showErrors && fieldErrors.deliveryList && (
                  <div className="edit-purchase-orders-field-error">{fieldErrors.deliveryList}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ACTIONS */}
        <div className="edit-purchase-orders-actions">
          <button
            type="button"
            className="edit-purchase-orders-secondary-btn"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            {t("actions.cancel")}
          </button>

          <button type="submit" className="edit-purchase-orders-primary-btn" disabled={saving || fetching}>
            {saving ? t("actions.saving") : t("actions.save", { defaultValue: "Guardar Alterações" })}
          </button>
        </div>
      </form>
    </div>
  </div>
);
};